<template>
  <div class="w-full !w-[360px] flex flex-col">
    <div
      v-if="
        !isDirectPayEnabled &&
        !isLoadingDirectDebits &&
        !isErrorLoadingDirectDebits
      "
      class="relative mb-3 h-16 shrink-0 overflow-hidden"
    >
      <Image
        src="pay-fast-deposit-banner"
        alt=""
        class="cursor-pointer h-full w-full select-none"
        format="png"
        @click="
          () =>
            openDirectDebitDialog({
              isActiveDirectDebit: false,
            })
        "
      />
    </div>

    <VeeForm v-slot="{ errors, handleSubmit, meta }">
      <form
        autocomplete="off"
        data-testid="form"
        class="flex flex-col !w-[360px]"
        @submit="handleSubmit($event, onSubmit)"
      >
        <section
          class="bg-neutral-secondary flex flex-col gap-2 justify-between rounded-xl min-h-[153px] p-4 border border-neutral-tertiary"
        >
          <div>
            <NText
              variant="caption1"
              weight="medium"
              class="block text-right mb-3 text-neutral-primary"
            >
              {{ t('deposit.deposit_amount') }}
            </NText>

            <VeeField
              v-slot="{ field }"
              v-model="formModel.totalAmount"
              name="totalAmount"
              :rules="
                formModel.selectedGateway === Number(directDebits?.[0]?.id)
                  ? {
                      required: true,
                      between: [
                        DEPOSIT_RANGE.minimum,
                        directDebits ? DEPOSIT_RANGE.maximum : Infinity,
                        t('deposit.deposit_amount'),
                        t('misc.rial'),
                      ],
                    }
                  : {
                      required: true,
                      min: [
                        DEPOSIT_RANGE.minimum,
                        t('deposit.deposit_amount'),
                        t('misc.rial'),
                      ],
                    }
              "
            >
              <NInput
                v-model="formModel.totalAmount"
                v-mask="{
                  alias: 'positiveNumber',
                  showMaskOnHover: false,
                }"
                :placeholder="t('deposit.enter_amount')"
                input-class="text-right"
                v-bind="field"
                :error="errors.totalAmount"
                :show-error="false"
                :required-sign="false"
                type="tel"
                dir="ltr"
                required
                size="small"
                data-testid="total-amount"
                :change-value-on-mouse-wheel="false"
                @focusin="$event.target.select()"
              >
                <template #postfix>
                  <NText
                    variant="caption1"
                    class="text-neutral-secondary-press"
                  >
                    {{ t('misc.rial') }}
                  </NText>
                </template>
              </NInput>

              <div
                v-if="errors.totalAmount"
                class="flex items-center justify-center gap-1 mt-2"
              >
                <NegarErrorLightIcon
                  size="16"
                  class="shrink-0 text-critical-primary"
                />
                <NText variant="overline" class="text-critical-primary">
                  {{ errors.totalAmount }}
                </NText>
              </div>

              <NText
                v-if="formModel.totalAmount"
                class="block text-center mt-2"
                variant="caption1"
                data-testid="amount-in-words"
              >
                {{ amountInWords }}
                {{ t('misc.toman') }}
              </NText>
            </VeeField>
          </div>

          <div class="flex flex-wrap justify-between gap-2">
            <NCheap
              v-for="item in SUGGESTED_DEPOSIT_AMOUNTS"
              :key="item.amount"
              color="gray"
              size="small"
              variant="outline"
              :closable="false"
              :label="item.label"
              class="cursor-pointer !text-neutral-secondary"
              @click="formModel.totalAmount = item.amount"
            >
              <NText
                variant="overline"
                weight="medium"
                class="!text-neutral-secondary"
              >
                {{ item.label }}
              </NText>
            </NCheap>
          </div>
        </section>

        <section
          class="bg-neutral-primary mx-3 mb-6 overflow-hidden rounded-none rounded-b-xl border border-t-0 border-neutral-tertiary"
        >
          <VeeField
            v-slot="{ field }"
            v-model="formModel.selectedGateway"
            name="selectedGateway"
            rules="required"
          >
            <NRadioGroup
              v-model="formModel.selectedGateway"
              v-bind="field"
              :error="errors.selectedGateway"
              :show-error="true"
              :required-sign="false"
              required
              data-testid="selected-gateway"
              class="!mb-0"
            >
              <div class="flex flex-col gap-3 p-3">
                <NRadio
                  :value="
                    banksInformations.find(
                      (item) =>
                        !item.directDebits &&
                        item.ipgId === formModel.selectedGateway
                    )?.ipgId ??
                    banksInformations.find((item) => !item.directDebits)?.ipgId
                  "
                  class="w-full"
                >
                  <NText
                    variant="caption1"
                    weight="medium"
                    class="text-neutral-primary"
                  >
                    {{ t('deposit.payment_gateway') }}
                  </NText>
                </NRadio>

                <div class="mr-4">
                  <NegarCreditCardIcon
                    v-if="isPendingBanksInformation && isLoadingDirectDebits"
                  />

                  <div v-else class="flex items-center gap-2">
                    <NButton
                      v-for="gateway in banksInformations.filter(
                        (item) => !item.directDebits
                      )"
                      :key="gateway.ipgId"
                      v-gid="{
                        module: 'cash',
                        screen: 'home',
                        section: 'depositDialog',
                        component: `${gateway.title}GatewayRadioButton`,
                      }"
                      type="button"
                      size="small"
                      variant="outline"
                      class="flex h-10 items-center justify-center rounded-lg border bg-neutral-primary px-2 transition-all duration-300 ease-in-out"
                      :class="
                        formModel.selectedGateway === gateway.ipgId
                          ? 'border-brand-primary'
                          : 'border-neutral-secondary hover:border-neutral-secondary-press'
                      "
                      @click.stop="formModel.selectedGateway = gateway.ipgId"
                    >
                      <span class="flex items-center">
                        <component
                          :is="BANK_ICONS[gateway.ipgId] || BANK_ICONS[0]"
                          class="shrink-0"
                          size="24px"
                          color="#509073"
                        />
                        <span
                          class="grid overflow-hidden transition-all duration-300 ease-in-out"
                          :class="
                            formModel.selectedGateway === gateway.ipgId
                              ? 'grid-cols-[1fr] opacity-100'
                              : 'grid-cols-[0fr] opacity-0'
                          "
                        >
                          <span class="min-w-0 overflow-hidden">
                            <NText
                              variant="caption2"
                              weight="medium"
                              class="whitespace-nowrap text-neutral-primary ps-2"
                            >
                              {{ gateway.persianTitle }}
                            </NText>
                          </span>
                        </span>
                      </span>
                    </NButton>
                  </div>
                </div>
              </div>

              <div
                class="flex items-center gap-3 border-t border-neutral-secondary p-3 justify-between"
              >
                <NRadio
                  :value="directDebits?.[0]?.id ?? t('deposit.directPayTitle')"
                  :disabled="!isDirectPayEnabled"
                  class="flex justify-between"
                  :class="
                    !isDirectPayEnabled ? '!cursor-default' : 'cursor-pointer'
                  "
                >
                  <NText
                    variant="caption1"
                    weight="medium"
                    :class="
                      directDebits?.[0]
                        ? 'text-neutral-primary'
                        : 'text-neutral-secondary'
                    "
                  >
                    {{ t('deposit.directPayTitle') }}
                  </NText>
                </NRadio>

                <div class="flex items-center gap-1">
                  <template v-if="isDirectPayEnabled && directDebits?.[0]">
                    <DirectDebitIcon class="text-brand-primary" size="20" />

                    <NText variant="caption2" weight="bold">
                      {{ BANKS[directDebits[0].bank]?.title }}
                    </NText>

                    <NegarTrashLightIcon
                      size="20"
                      class="cursor-pointer"
                      @click.stop="
                        openCancelDirectDebitDialog(directDebits[0].uId)
                      "
                    />
                  </template>

                  <NButton
                    v-else
                    v-gid="{
                      module: 'cash',
                      screen: 'home',
                      section: 'depositDialog',
                      component: 'directDebitDescriptionDialogButton',
                    }"
                    type="button"
                    variant="plain"
                    size="mini"
                    class="inline-flex items-center !text-blue-primary !px-0"
                    @click.stop="
                      openDirectDebitDialog({
                        isActiveDirectDebit: false,
                      })
                    "
                  >
                    <NText variant="caption1" weight="bold">
                      {{ t('deposit.activate') }}
                    </NText>

                    <NegarArrowLeftIcon size="20" class="mr-0.5" />
                  </NButton>
                </div>
              </div>
            </NRadioGroup>
          </VeeField>
        </section>

        <div>
          <NBox
            v-if="!syncOnlineStatus"
            class="flex items-start gap-x-1 px-2 py-3"
          >
            <NegarErrorLightIcon class="text-warning-secondary" size="16" />

            <NText variant="overline" weight="medium">
              {{ t('deposit.depositSyncMessage') }}
            </NText>
          </NBox>

          <NButton
            v-gid="{
              module: 'cash',
              screen: 'home',
              section: 'depositDialog',
              component: 'submitButton',
              disabled: isCreatingBankPaymentToken || !meta.valid,
            }"
            class="w-full"
            :loading="isCreatingBankPaymentToken || isCreateDirectDebit"
            :disabled="isCreatingBankPaymentToken || !meta.valid"
            type="submit"
            data-testid="submit"
          >
            {{ t('deposit.pay') }}
          </NButton>
        </div>
      </form>
    </VeeForm>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { num2mixed } from 'num2persian';
import { NButton, useDialog, useToast } from '@negar/core';
import {
  createDepositRedirectURL,
  useGetDirectDebitsQuery,
  useCreateDirectDebitQuery,
  useGetBanksInformationQuery,
  useCreateBankPaymentTokenQuery,
  useGetSyncOnlineStatus,
} from '@core/cash';
import {
  NegarCreditCardIcon,
  NegarTrashLightIcon,
  NegarErrorLightIcon,
  NegarArrowLeftIcon,
} from '@negar/icons';
import { useQueryClient } from '@libs/use';
import { useCashDialogs } from '@/modules/cash';
import Image from '@/components/Image.vue';
import vMask from '@/directives/mask';
import {
  DEPOSIT_RANGE,
  SUGGESTED_DEPOSIT_AMOUNTS,
  BANKS,
  BANK_ICONS,
  IPGS,
} from '../constants';

const { t } = useI18n();
const toast = useToast();
const { remove } = useDialog();
const queryClient = useQueryClient();
const { openDirectDebitDialog, openCancelDirectDebitDialog } = useCashDialogs();

type FormModel = { selectedGateway: number | null; totalAmount: number | null };

const props = defineProps<{ totalAmount: number }>();

const formModel = reactive<FormModel>({
  selectedGateway: null,
  totalAmount: null,
});

const { directDebits, isLoadingDirectDebits, isErrorLoadingDirectDebits } =
  useGetDirectDebitsQuery();

const { syncOnlineStatus } = useGetSyncOnlineStatus();

const { banksInformations, isPendingBanksInformation } =
  useGetBanksInformationQuery({
    directDebitContract: computed(() => directDebits.value?.[0]),
    enabled: computed(() => true),
    onSuccess(data) {
      const activeItem = data?.data?.[0]?.ipgId;
      formModel.selectedGateway = activeItem;
    },
  });

const amountInWords = computed(() => {
  if (formModel.totalAmount !== null && formModel.totalAmount !== undefined) {
    return num2mixed(Math.round(formModel.totalAmount / 10));
  }
  return null;
});

const { createBankPaymentToken, isCreatingBankPaymentToken } =
  useCreateBankPaymentTokenQuery({
    onSuccess(result) {
      if (result.data.token) {
        const isValidUrl = (result.data.token as string).startsWith('https');
        window.location.assign(
          isValidUrl
            ? result.data.token
            : createDepositRedirectURL(result.data.token)
        );
      }
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({ message, duration: 3000 });
        }
      }
    },
  });

const { createDirectDebit, isCreateDirectDebit } = useCreateDirectDebitQuery({
  onSuccess() {
    toast.positive({
      message: t('deposit.directDebitSuccessMessage', {
        amount: formModel.totalAmount,
        bankName: BANKS[directDebits.value[0].bank].title,
        duration: 10000,
      }),
    });
    remove();
    queryClient.invalidateQueries(['cash']);
  },
  onReject(error) {
    if (error) {
      for (const { message } of error.response.data) {
        toast.negative({ message, duration: 10000 });
      }
    }
  },
});

const onSubmit = () => {
  if (
    directDebits.value.length &&
    !!directDebits.value?.find((item) => item.id === formModel.selectedGateway)
  ) {
    return createDirectDebit({
      amount: formModel.totalAmount,
      traceId: directDebits.value[0].traceId,
    });
  }
  const bankName = IPGS[formModel.selectedGateway || 0];
  return createBankPaymentToken({ ...formModel, bankName });
};

const DirectDebitIcon = () => BANKS[directDebits.value[0].bank].icon;

const isDirectPayEnabled = computed(() => {
  return directDebits.value[0]?.enabled === true;
});
</script>
